import React from 'react';
import { Card, Button, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  FileTextOutlined,
  UserOutlined,
  TagsOutlined,
  MedicineBoxOutlined,
  FolderOutlined,
  ExclamationCircleOutlined,
  SafetyCertificateOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  HeartOutlined,
  ShakeOutlined,
  BookOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  MessageOutlined,
  HomeOutlined,
  DatabaseOutlined,
  PlusOutlined
} from '@ant-design/icons';
import '../style.scss';

interface MasterDataManagementProps {
  currentUser?: {
    role: string;
    fullname: string;
    email?: string;
  };
}

interface ManagementTile {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  bgColor: string;
  onClick: () => void;
}

const MasterDataManagement: React.FC<MasterDataManagementProps> = ({ currentUser }) => {
  const navigate = useNavigate();
  
  const handleTileClick = (tileId: string) => {
    console.log(`Clicked on ${tileId}`);
    
    // Handle specific tile clicks
    switch (tileId) {
      case 'create-new-case':
        navigate('/create-case');
        break;
      default:
        // TODO: Implement navigation to other specific management pages
        break;
    }
  };

  const managementTiles: ManagementTile[] = [
    {
      id: 'create-new-case',
      title: 'Begin New Interview',
      description: 'Create and manage discrimination cases',
      icon: PlusOutlined,
      color: '#1890ff',
      bgColor: 'blue-bg',
      onClick: () => handleTileClick('create-new-case')
    },
    {
      id: 'accused-issues',
      title: 'Accused Issues Management',
      description: 'Manage accused issues and related data',
      icon: FileTextOutlined,
      color: '#dc2626',
      bgColor: 'red-bg',
      onClick: () => handleTileClick('accused-issues')
    },
    {
      id: 'accused-parties',
      title: 'Accused Parties Management',
      description: 'Manage accused parties information',
      icon: UserOutlined,
      color: '#ea580c',
      bgColor: 'orange-bg',
      onClick: () => handleTileClick('accused-parties')
    },
    {
      id: 'accused-categories',
      title: 'Accused Categories Management',
      description: 'Manage accused categories and classifications',
      icon: TagsOutlined,
      color: '#eab308',
      bgColor: 'yellow-bg',
      onClick: () => handleTileClick('accused-categories')
    },
    {
      id: 'body-parts-touched',
      title: 'Body Parts Touched Management',
      description: 'Manage body parts touched data',
      icon: MedicineBoxOutlined,
      color: '#16a34a',
      bgColor: 'green-bg',
      onClick: () => handleTileClick('body-parts-touched')
    },
    {
      id: 'case-types',
      title: 'Case Types Management',
      description: 'Manage different case types',
      icon: FolderOutlined,
      color: '#2563eb',
      bgColor: 'blue-bg',
      onClick: () => handleTileClick('case-types')
    },
    {
      id: 'disability-effects',
      title: 'Disability Effects Management',
      description: 'Manage disability effects information',
      icon: ExclamationCircleOutlined,
      color: '#7c3aed',
      bgColor: 'purple-bg',
      onClick: () => handleTileClick('disability-effects')
    },
    {
      id: 'discrimination-bases',
      title: 'Discrimination Bases Management',
      description: 'Manage discrimination bases data',
      icon: SafetyCertificateOutlined,
      color: '#dc2626',
      bgColor: 'red-bg',
      onClick: () => handleTileClick('discrimination-bases')
    },
    {
      id: 'employer-action-reasons',
      title: 'Employer Action Reasons Management',
      description: 'Manage employer action reasons',
      icon: ToolOutlined,
      color: '#ea580c',
      bgColor: 'orange-bg',
      onClick: () => handleTileClick('employer-action-reasons')
    },
    {
      id: 'further-results',
      title: 'Further Results Management',
      description: 'Manage further results data',
      icon: CheckCircleOutlined,
      color: '#eab308',
      bgColor: 'yellow-bg',
      onClick: () => handleTileClick('further-results')
    },
    {
      id: 'handicap-types',
      title: 'Handicap Types Management',
      description: 'Manage handicap types information',
      icon: HeartOutlined,
      color: '#16a34a',
      bgColor: 'green-bg',
      onClick: () => handleTileClick('handicap-types')
    },
    {
      id: 'harassment-actions',
      title: 'Harassment Actions Management',
      description: 'Manage harassment actions data',
      icon: ShakeOutlined,
      color: '#2563eb',
      bgColor: 'blue-bg',
      onClick: () => handleTileClick('harassment-actions')
    },
    {
      id: 'learn-of-it-sources',
      title: 'Learn of It Sources Management',
      description: 'Manage learn of it sources',
      icon: BookOutlined,
      color: '#7c3aed',
      bgColor: 'purple-bg',
      onClick: () => handleTileClick('learn-of-it-sources')
    },
    {
      id: 'losses-time-lost',
      title: 'Losses & Time Lost Management',
      description: 'Manage losses and time lost data',
      icon: ClockCircleOutlined,
      color: '#dc2626',
      bgColor: 'red-bg',
      onClick: () => handleTileClick('losses-time-lost')
    },
    {
      id: 'offensive-places',
      title: 'Offensive Places Management',
      description: 'Manage offensive places information',
      icon: EnvironmentOutlined,
      color: '#ea580c',
      bgColor: 'orange-bg',
      onClick: () => handleTileClick('offensive-places')
    },
    {
      id: 'response-management',
      title: 'Response Management',
      description: 'Manage response data and information',
      icon: MessageOutlined,
      color: '#eab308',
      bgColor: 'yellow-bg',
      onClick: () => handleTileClick('response-management')
    }
  ];

  return (
    <div>
      {/* Header Card */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ padding: '16px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <DatabaseOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
              <span style={{ fontSize: '24px', fontWeight: 600, color: '#000' }}>Master Data Management</span>
            </div>
            <Space>
              <Button 
                icon={<HomeOutlined />} 
                onClick={() => navigate('/')}
                type="default"
              >
                Home
              </Button>
            </Space>
          </div>
          <span style={{ fontSize: '14px', color: '#666' }}>
            Manage all system master data and configurations for the employee management system
          </span>
        </div>
      </Card>

      {/* Main Content */}
      <Card>
        <div className="management-tiles-grid">
          {managementTiles.map((tile) => {
            const IconComponent = tile.icon;
            return (
              <Card
                key={tile.id}
                className={`management-tile ${tile.bgColor}`}
                hoverable
                onClick={tile.onClick}
                style={{
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  height: '140px'
                }}
                bodyStyle={{
                  padding: '20px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <div className="tile-content">
                  <div className="tile-icon-wrapper" style={{ marginBottom: '12px' }}>
                    <IconComponent 
                      style={{ 
                        fontSize: '24px', 
                        color: tile.color 
                      } as React.CSSProperties} 
                    />
                  </div>
                  <div className="tile-text">
                    <h3 className="tile-title" style={{
                      margin: 0,
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#1f2937',
                      marginBottom: '4px',
                      lineHeight: '1.3'
                    }}>
                      {tile.title}
                    </h3>
                    <p className="tile-description" style={{
                      margin: 0,
                      fontSize: '12px',
                      color: '#6b7280',
                      lineHeight: '1.4'
                    }}>
                      {tile.description}
                    </p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default MasterDataManagement;
